import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';

// The connections flagship flow: pick a HubSpot deal → its context (company,
// value, stage, contacts, notes) lands in the prompt and the standard
// generation flow builds a personalized proposal around it.
export default function HubSpotDealPickerModal({ userId, onPicked, onClose }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingId, setFetchingId] = useState(null);
  const [query, setQuery] = useState('');
  const searchTimer = useRef(null);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ user_id: userId });
      if (q) params.set('q', q);
      const res = await fetch(withBackend(`/api/hubspot/deals?${params}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load deals');
      setDeals(data.deals || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load deals');
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (value) => {
    setQuery(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(value.trim()), 400);
  };

  const pick = async (deal) => {
    setFetchingId(deal.id);
    try {
      const res = await fetch(withBackend('/api/hubspot/deal-context'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, deal_id: deal.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load deal details');
      onPicked?.(data.context_text, data.deal);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed to load deal details');
    } finally {
      setFetchingId(null);
    }
  };

  const fmtAmount = (a) => {
    const n = Number(a);
    return Number.isFinite(n) && n > 0 ? n.toLocaleString() : null;
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Pick a HubSpot deal"
    >
      <div
        className="flex max-h-[70vh] w-full max-w-lg flex-col rounded-2xl border border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-5 py-4">
          <h2 className="text-base font-semibold text-foreground">Create from a HubSpot deal</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-border/60 px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search deals…"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading deals…
            </div>
          ) : deals.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {query ? 'No deals match that search.' : 'No deals found in your HubSpot portal.'}
            </p>
          ) : (
            <div className="space-y-1">
              {deals.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pick(d)}
                  disabled={fetchingId !== null}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted/60 disabled:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{d.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {[d.company, fmtAmount(d.amount), d.stage].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  {fetchingId === d.id && (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="border-t border-border/60 px-5 py-3 text-[11px] text-muted-foreground">
          The deal's company, value, stage, contacts, and notes are added to your prompt —
          then the normal generation flow takes over.
        </p>
      </div>
    </div>
  );
}
